/**
 * Regicide game view.
 *
 * Reuses CircleTable, Hand, PlayingCard. Game-specific bits:
 *   - up-card display + pickup/pass/dealer-choose-trump action bars,
 *   - discard action bar,
 *   - after-trick action bar (suit picker + rank-direction toggle),
 *   - trump + rank-direction indicator pinned to the table centre.
 *
 * Rank in Regicide can flip mid-round: when reversed, A is "high" (just
 * below the joker). The Hand's existing sort isn't aware of this — the
 * player can re-read it via the visible rank indicator above the table.
 */

import { useMemo, useState } from "react";
import {
  lerpCardWidth,
  useDynamicTableTop,
  useResetOnChange,
  useWindowWidth,
} from "../../lib/uiHooks.js";
import type {
  RegicidePublicState,
} from "../../../../shared/games/regicide.js";
import type { CardId, SuitId } from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard } from "../../components/PlayingCard.js";
import { RegicideScoreBar } from "./RegicideScoreBar.js";
import { RegicideTrumpPanel } from "./RegicideTrumpPanel.js";

interface RegicideGameProps {
  state: RegicidePublicState;
  yourHand: CardId[];
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

const SUITS: SuitId[] = ["C", "D", "H", "S"];
const SUIT_GLYPH: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };
const SUIT_NAME: Record<string, string> = {
  C: "Clubs", D: "Diamonds", H: "Hearts", S: "Spades",
};
const SUIT_RED = (s: string) => s === "H" || s === "D";

export function RegicideGame({
  state,
  yourHand,
  yourSeat,
  send,
  mobile = false,
}: RegicideGameProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const phaseKey =
    `${state.phase}:${state.roundNumber}:${state.currentTrickPlays.length}:${state.completedTricks.length}`;
  useResetOnChange(phaseKey, () => setSelected(new Set()));

  const windowWidth = useWindowWidth();
  const playZoneCardWidth = mobile ? 78 : lerpCardWidth(windowWidth, 90, 132);

  const legal = useMemo(() => {
    if (yourSeat === null) return new Set<CardId>();
    if (state.phase === "discard") return new Set(yourHand);
    if (state.phase !== "play") return new Set(yourHand);
    if (state.currentSeat !== yourSeat) return new Set<CardId>();
    return computeRegicideLegal(yourHand, state);
  }, [yourHand, state, yourSeat]);

  const selectedCardIds = useMemo<CardId[]>(
    () => [...selected].map((i) => yourHand[i]!).filter(Boolean),
    [selected, yourHand],
  );

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      if (next.size > 1) {
        for (const x of [...next]) if (x !== i) next.delete(x);
      }
      return next;
    });
  }

  const isMyTurn = yourSeat !== null && state.currentSeat === yourSeat;
  const showHand = yourSeat !== null;
  const tableTopRef = useDynamicTableTop();

  return (
    <div className="game-view regicide-view" ref={tableTopRef}>
      <RegicideScoreBar state={state} yourSeat={yourSeat} />
      <RegicideTrumpPanel state={state} />

      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <RegicideSeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            playZoneCardWidth={playZoneCardWidth}
          />
        )}
      >
        <RegicideCenter state={state} cardWidth={playZoneCardWidth} />
      </CircleTable>

      {isMyTurn && state.phase === "pickup_choice" && (
        <PickupActionBar state={state} send={send} />
      )}
      {isMyTurn && state.phase === "discard" && (
        <DiscardActionBar
          selectedCard={selectedCardIds[0] ?? null}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {isMyTurn && state.phase === "dealer_choose_trump" && (
        <ChooseTrumpActionBar send={send} />
      )}
      {isMyTurn && state.phase === "play" && (
        <PlayActionBar
          selectedCard={selectedCardIds[0] ?? null}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {isMyTurn && state.phase === "after_trick" && (
        <AfterTrickActionBar state={state} send={send} />
      )}
      {yourSeat !== null && state.phase === "round_end" && (
        <ReadyActionBar send={send} />
      )}

      {showHand && (
        <Hand
          cards={yourHand}
          legal={
            state.phase === "play" || state.phase === "discard" ? legal : undefined
          }
          selected={selected}
          onToggle={toggle}
          sort="aces-low"
          mobile={mobile}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat view
// ---------------------------------------------------------------------------

function RegicideSeatView({
  seat,
  state,
  yourSeat,
  playZoneCardWidth,
}: {
  seat: number;
  state: RegicidePublicState;
  yourSeat: number | null;
  playZoneCardWidth: number;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const isTurn = state.currentSeat === seat;
  const isDealer = seat === state.dealer;
  const isPickup = seat === state.pickedUpBy;
  const trickPlay = state.currentTrickPlays.find((tp) => tp.seat === seat);
  const isWinning =
    state.currentTrickPlays.length > 0 &&
    state.currentTrickPlays[state.currentTrickWinningIndex]?.seat === seat;
  return (
    <>
      <div
        className={[
          "nameplate",
          isMe ? "mine" : "opponent",
          isTurn ? "turn" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
          {isDealer && <span className="role-tag">DEALER</span>}
          {isPickup && <span className="role-tag">PICKED UP</span>}
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">tricks {p.tricksTaken}</span>
          <span className="rank-line">total {p.totalScore}</span>
          {state.phase === "pickup_choice" && p.passedPickup && (
            <span className="rank-line">passed</span>
          )}
        </div>
      </div>
      <div className={`play-zone ${isWinning ? "winning" : ""}`}>
        {isWinning && <div className="winning-badge">WINNING</div>}
        {trickPlay && (
          <div className="play-card-wrapper" style={{ position: "relative", zIndex: 1 }}>
            <PlayingCard card={trickPlay.card} width={playZoneCardWidth} />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Centre — show the up-card during pickup, trump glyph during play.
// ---------------------------------------------------------------------------

function RegicideCenter({
  state,
  cardWidth,
}: {
  state: RegicidePublicState;
  cardWidth: number;
}) {
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Match complete</span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    const last = state.history[state.history.length - 1];
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Round complete</span>
        {last && (
          <span className="points" style={{ fontSize: 13 }}>
            {state.players.map((p, i) =>
              `${p.displayName} ${last.tricks[i]} (+${last.delta[i]})`,
            ).join(" · ")}
            {last.sweep && " · SWEEP!"}
          </span>
        )}
      </div>
    );
  }
  if (state.phase === "pickup_choice" || state.phase === "discard") {
    if (state.upCard) {
      return (
        <div className="center-pile" style={{ flexDirection: "column", padding: 10, gap: 6 }}>
          <PlayingCard card={state.upCard} width={Math.round(cardWidth * 0.62)} />
          <span className="label" style={{ fontSize: 12 }}>
            UP CARD{state.upCardIsJoker ? " (JOKER!)" : ""}
          </span>
        </div>
      );
    }
    return null;
  }
  if (state.phase === "dealer_choose_trump") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 10 }}>
        <span className="label">Everyone passed</span>
        <span className="label" style={{ fontSize: 12 }}>Dealer is choosing trump…</span>
      </div>
    );
  }
  // play / after_trick
  return null;
}

// ---------------------------------------------------------------------------
// Action bars
// ---------------------------------------------------------------------------

function PickupActionBar({
  state,
  send,
}: {
  state: RegicidePublicState;
  send: (m: ClientMsg) => void;
}) {
  const upGlyph = state.upCard ? SUIT_GLYPH[state.upCard[1]!] ?? "" : "";
  if (state.upCardIsJoker) {
    return (
      <div className="action-bar" style={{ flexWrap: "wrap", justifyContent: "center", gap: 6 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          Pick up the joker and name trump:
        </span>
        {SUITS.map((s) => (
          <button
            key={s}
            onClick={() =>
              send({
                t: "action",
                action: { kind: "regicide", payload: { type: "pickup", trumpSuit: s } },
              })
            }
            style={{ color: SUIT_RED(s) ? "#c81818" : undefined }}
          >
            Take · {SUIT_GLYPH[s]}
          </button>
        ))}
        <button
          onClick={() =>
            send({ t: "action", action: { kind: "regicide", payload: { type: "pass_pickup" } } })
          }
        >
          Pass
        </button>
      </div>
    );
  }
  return (
    <div className="action-bar" style={{ gap: 8 }}>
      <button
        className="primary"
        onClick={() =>
          send({ t: "action", action: { kind: "regicide", payload: { type: "pickup" } } })
        }
        style={{ color: state.upCard && SUIT_RED(state.upCard[1]!) ? "#c81818" : undefined }}
      >
        Take up-card · {upGlyph} (trump)
      </button>
      <button
        onClick={() =>
          send({ t: "action", action: { kind: "regicide", payload: { type: "pass_pickup" } } })
        }
      >
        Pass
      </button>
    </div>
  );
}

function DiscardActionBar({
  selectedCard,
  send,
  onAfterSubmit,
}: {
  selectedCard: CardId | null;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!selectedCard}
        onClick={() => {
          if (!selectedCard) return;
          send({
            t: "action",
            action: { kind: "regicide", payload: { type: "discard", card: selectedCard } },
          });
          onAfterSubmit();
        }}
      >
        Discard
      </button>
    </div>
  );
}

function ChooseTrumpActionBar({ send }: { send: (m: ClientMsg) => void }) {
  return (
    <div className="action-bar" style={{ gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
      <span className="muted" style={{ fontSize: 12 }}>Choose trump:</span>
      {SUITS.map((s) => (
        <button
          key={s}
          onClick={() =>
            send({ t: "action", action: { kind: "regicide", payload: { type: "choose_trump", suit: s } } })
          }
          style={{ color: SUIT_RED(s) ? "#c81818" : undefined }}
        >
          {SUIT_GLYPH[s]} {SUIT_NAME[s]}
        </button>
      ))}
    </div>
  );
}

function PlayActionBar({
  selectedCard,
  send,
  onAfterSubmit,
}: {
  selectedCard: CardId | null;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!selectedCard}
        onClick={() => {
          if (!selectedCard) return;
          send({
            t: "action",
            action: { kind: "regicide", payload: { type: "play", card: selectedCard } },
          });
          onAfterSubmit();
        }}
      >
        Play card
      </button>
    </div>
  );
}

function AfterTrickActionBar({
  state,
  send,
}: {
  state: RegicidePublicState;
  send: (m: ClientMsg) => void;
}) {
  // Use local state so the player can preview a choice before committing.
  const [suit, setSuit] = useState<SuitId>(state.trumpSuit ?? "S");
  const [reversed, setReversed] = useState(state.rankReversed);
  return (
    <div
      className="action-bar"
      style={{ flexWrap: "wrap", justifyContent: "center", gap: 6 }}
    >
      <span className="muted" style={{ fontSize: 12 }}>You won — set trump:</span>
      {SUITS.map((s) => {
        const sel = suit === s;
        return (
          <button
            key={s}
            onClick={() => setSuit(s)}
            style={{
              color: SUIT_RED(s) ? "#c81818" : undefined,
              borderColor: sel ? "var(--my-team)" : undefined,
              boxShadow: sel ? "0 0 0 1px var(--my-team)" : undefined,
            }}
          >
            {SUIT_GLYPH[s]}
          </button>
        );
      })}
      <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>Rank:</span>
      <button
        onClick={() => setReversed(false)}
        style={{
          borderColor: !reversed ? "var(--my-team)" : undefined,
          boxShadow: !reversed ? "0 0 0 1px var(--my-team)" : undefined,
        }}
        title="Joker high, K next, 9..2, A low"
      >
        Normal (K high)
      </button>
      <button
        onClick={() => setReversed(true)}
        style={{
          borderColor: reversed ? "var(--my-team)" : undefined,
          boxShadow: reversed ? "0 0 0 1px var(--my-team)" : undefined,
        }}
        title="Joker high, A next, 2..9, K low"
      >
        Reversed (A high)
      </button>
      <button
        className="primary"
        onClick={() =>
          send({
            t: "action",
            action: {
              kind: "regicide",
              payload: { type: "after_trick", trumpSuit: suit, rankReversed: reversed },
            },
          })
        }
      >
        Lead next trick
      </button>
    </div>
  );
}

function ReadyActionBar({ send }: { send: (m: ClientMsg) => void }) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        onClick={() =>
          send({ t: "action", action: { kind: "regicide", payload: { type: "ready_next_round" } } })
        }
      >
        Ready for next round
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legality hint — must follow led suit if able; joker has no suit.
// ---------------------------------------------------------------------------

function computeRegicideLegal(
  hand: CardId[],
  state: RegicidePublicState,
): Set<CardId> {
  const out = new Set<CardId>();
  const trick = state.currentTrickPlays;
  if (trick.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }
  const ledId = trick[0]!.card;
  // Joker has no suit — anything is legal.
  if (ledId === "SJ" || ledId === "BJ") {
    for (const c of hand) out.add(c);
    return out;
  }
  const ledSuit = ledId[1]!;
  // Cards whose effective suit matches the led suit (jokers don't match).
  const inSuit = hand.filter((c) => c !== "SJ" && c !== "BJ" && c[1] === ledSuit);
  if (inSuit.length > 0) {
    for (const c of inSuit) out.add(c);
  } else {
    for (const c of hand) out.add(c);
  }
  return out;
}
